import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

// Sumsub WebSDK loader + mount. Reads a short-lived access token from
// the `sumsub-token` edge function and launches the verification flow.
const SDK_URL = "https://static.sumsub.com/idensic/static/sns-websdk-builder.js";

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    snsWebSdk?: any;
  }
}

function loadSdk(): Promise<void> {
  if (window.snsWebSdk) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SDK_URL;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load Sumsub SDK"));
    document.head.appendChild(s);
  });
}

async function fetchToken(): Promise<string> {
  const { data, error } = await supabase.functions.invoke("sumsub-token", { body: {} });
  if (error) throw new Error(error.message);
  if (!data?.token) throw new Error("No token returned");
  return data.token as string;
}

export const SumsubVerify = ({ onSubmitted }: { onSubmitted?: () => void }) => {
  const [launching, setLaunching] = useState(false);
  const [launched, setLaunched] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => () => {
    // best-effort cleanup
    if (containerRef.current) containerRef.current.innerHTML = "";
  }, []);

  const launch = async () => {
    setLaunching(true);
    try {
      await loadSdk();
      const token = await fetchToken();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sdk = (window as any).snsWebSdk
        .init(token, async () => await fetchToken())
        .withConf({ lang: "en", theme: "dark" })
        .withOptions({ addViewportTag: false, adaptIframeHeight: true })
        .on("idCheck.onApplicantSubmitted", () => {
          toast.success("Submitted for review");
          onSubmitted?.();
        })
        .on("idCheck.onError", (e: unknown) => {
          console.error("Sumsub error", e);
          toast.error("Verification error");
        })
        .build();
      sdk.launch("#sumsub-websdk-container");
      setLaunched(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not start verification");
    } finally {
      setLaunching(false);
    }
  };

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-start gap-2">
        <ShieldCheck className="h-5 w-5 text-primary mt-0.5" />
        <div className="flex-1">
          <div className="font-medium text-sm">Instant verification</div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Snap your ID and a selfie. Most decisions are returned in under a minute.
          </p>
        </div>
      </div>
      {!launched && (
        <Button onClick={launch} disabled={launching} className="w-full">
          {launching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {launching ? "Starting…" : "Verify with Sumsub"}
        </Button>
      )}
      <div
        id="sumsub-websdk-container"
        ref={containerRef}
        className="min-h-[1px]"
      />
    </div>
  );
};

export default SumsubVerify;
